/**
 * L'écran de pointage (la liste) : ce qu'il décide sans DOM, pour être éprouvé en environnement
 * node (SCAN-QR § 2.1 et § 7, défauts 1, 2 et 4).
 *
 * - la date revient au jour même au retour sur l'écran, sauf changée à la main (réencodage) ;
 * - le bandeau « Il est 16h07 : un ✓ ici compte ATL1 [Passer en ATL2] » ;
 * - les relectures numérotées : une réponse plus ancienne que la dernière demandée est ignorée ;
 * - le réessai d'un ✓ qui échoue pour une raison réseau (2 fois, 2 s puis 5 s) avant de l'annuler ;
 * - quand offrir « Scanner ».
 *
 * PROPOSITION TECHNIQUE, sauf mention.
 */
import { appliquerPointage, plagesDuJour, type LecturePointage, type PlageDuLien, type SourceDuPointage } from '../../lib/garderie/pointage';
import { plageDuScan, type ResultatDuScan } from '../../lib/garderie/scan';

/* ------------------------------------------------------------------------- */
/* La date                                                                    */
/* ------------------------------------------------------------------------- */

/**
 * La date à montrer au retour sur l'écran (SCAN-QR § 2.1) : une date **non changée à la main**
 * revient au jour même (un téléphone ouvert la veille pointe aujourd'hui) ; changée à la main,
 * elle reste, sous le bandeau « Vous pointez le … ». Jamais une date à venir.
 */
export function dateAuRetour(date: string, changeeALaMain: boolean, aujourdHui: string): string {
  if (date > aujourdHui) return aujourdHui;
  return changeeALaMain ? date : aujourdHui;
}

/** `AAAA-MM-JJ` → « 15/10 ». */
export function dateCourte(date: string): string {
  const [, m, j] = date.split('-');
  return m && j ? `${j}/${m}` : date;
}

/* ------------------------------------------------------------------------- */
/* La plage de la liste face à la règle                                       */
/* ------------------------------------------------------------------------- */

export interface BandeauDeLaPlage {
  /** « 16h07 ». */
  heure: string;
  /** La plage que la liste pointe. */
  ici: PlageDuLien;
  /** La plage qu'un scan poserait maintenant. */
  regle: PlageDuLien;
}

/**
 * Le bandeau « Il est 16h07 : un ✓ ici compte ATL1 [Passer en ATL2] » (SCAN-QR § 2.1) : seulement
 * aujourd'hui, quand la règle de plage (sans tenir compte du mode, tolérance comprise) donne une
 * plage du jour autre que celle de la liste. Rien quand la règle ne donne aucune plage (« à
 * vérifier », « pas de garderie ») : la liste reste où elle est. `heure` : `HH:MM[:SS]` de Bruxelles.
 */
export function bandeauDeLaPlage(lecture: LecturePointage | null, plageId: string | null, heure: string): BandeauDeLaPlage | null {
  if (!lecture || !lecture.aujourdHui || !plageId) return null;
  const duJour = plagesDuJour(lecture.plages, lecture.date);
  const ici = duJour.find((p) => p.id === plageId);
  if (!ici) return null;
  const r = plageDuScan(duJour, lecture.date, heure, null, lecture.ferie !== null);
  if (!('plage' in r) || r.plage.id === plageId) return null;
  return { heure: `${heure.slice(0, 2)}h${heure.slice(3, 5)}`, ici, regle: r.plage };
}

/* ------------------------------------------------------------------------- */
/* Les relectures numérotées                                                  */
/* ------------------------------------------------------------------------- */

/**
 * Les relectures de la liste sont numérotées (SCAN-QR § 7, 4) : seule la réponse à la dernière
 * demande est appliquée ; une réponse en retard n'écrase jamais une plus récente. Un geste (✓ ou
 * ✗) `invalide` les relectures déjà parties : leur réponse pourrait précéder le geste.
 */
export class CompteurDeRelectures {
  private derniere = 0;
  private plancher = 0;

  /** Une nouvelle relecture : son numéro. */
  demander(): number {
    this.derniere += 1;
    return this.derniere;
  }

  /** Les relectures déjà demandées ne s'appliqueront plus. */
  invalider(): void {
    this.plancher = this.derniere;
  }

  /** Vrai si la réponse de la relecture `n` peut s'appliquer. */
  estAJour(n: number): boolean {
    return n === this.derniere && n > this.plancher;
  }

  /** Vrai si `n` est la dernière demandée (même invalidée) : c'est elle qui éteint l'indicateur de relecture. */
  estLaDerniere(n: number): boolean {
    return n === this.derniere;
  }
}

/* ------------------------------------------------------------------------- */
/* Le réessai d'un geste                                                      */
/* ------------------------------------------------------------------------- */

/** Les délais avant chaque nouvel essai d'un ✓ qui a échoué pour une raison réseau : 2 s, puis 5 s. */
export const DELAIS_DE_REESSAI_MS: readonly number[] = [2000, 5000];

const pause = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * `f`, réessayée après chaque délai de `delais` tant qu'elle échoue d'une erreur `reessayable`
 * (le réseau) ; une autre erreur (un refus de la base) ou le dernier échec est rejeté tel quel.
 * Le geste de la base est idempotent (un second ✓ ne crée rien, un second ✗ ne retire rien),
 * le réessai ne double donc rien.
 */
export async function avecReessais<T>(
  f: () => Promise<T>,
  reessayable: (e: unknown) => boolean,
  delais: readonly number[] = DELAIS_DE_REESSAI_MS,
  attendre: (ms: number) => Promise<void> = pause,
): Promise<T> {
  for (let essai = 0; ; essai += 1) {
    try {
      return await f();
    } catch (e) {
      if (essai >= delais.length || !reessayable(e)) throw e;
      await attendre(delais[essai]);
    }
  }
}

/* ------------------------------------------------------------------------- */
/* « Scanner »                                                                */
/* ------------------------------------------------------------------------- */

/** « Scanner » s'offre quand la source sait scanner, sur la liste d'aujourd'hui, mois non facturé. */
export function peutScanner(source: Pick<SourceDuPointage, 'scanner'>, lecture: LecturePointage | null, aujourdHui: string): boolean {
  return !!source.scanner && !!lecture && lecture.aujourdHui && lecture.date === aujourdHui && !lecture.moisFacture;
}

/* ------------------------------------------------------------------------- */
/* Après un lot de scans                                                      */
/* ------------------------------------------------------------------------- */

/**
 * La liste après l'accusé d'un lot de scans, avant même de la relire : un scan pointé (ou déjà
 * pointé) coche l'enfant sur sa plage, une annulation acceptée le décoche. Seuls comptent les
 * résultats de la date de la liste, avec un enfant et une plage ; le reste ne touche à rien.
 */
export function appliquerLesScans(lecture: LecturePointage, traites: readonly ResultatDuScan[]): LecturePointage {
  let enfants = lecture.enfants;
  for (const t of traites) {
    if (t.date !== lecture.date || !t.enfant || !t.plageId) continue;
    if (!enfants.some((e) => e.id === t.enfant!.id)) continue;
    if (t.resultat === 'pointe' || t.resultat === 'deja_pointe') enfants = appliquerPointage(enfants, t.enfant.id, t.plageId, true);
    else if (t.resultat === 'annule') enfants = appliquerPointage(enfants, t.enfant.id, t.plageId, false);
  }
  return enfants === lecture.enfants ? lecture : { ...lecture, enfants };
}

/** Faut-il relire la liste après ce lot ? Oui dès qu'un scan a posé ou retiré une présence. */
export function lotAChangeLaListe(traites: readonly ResultatDuScan[]): boolean {
  return traites.some((t) => t.resultat === 'pointe' || t.resultat === 'annule');
}
